import React, { Suspense } from "react";
import { BrowserRouter } from "react-router-dom";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import keycloak, { keycloakInitOptions } from "./Keycloak";
import { TimezoneProvider } from "./context/TimezoneContext";
import { useTranslation } from "react-i18next";
import { useUiVersion } from "./components/common/useUiVersion";
import LazyTreeBoundary, { clearLazyTreeReloadLatch } from "./components/common/LazyTreeBoundary";
// GUNDI-5642: legacy is the default tree, so it is imported directly — every
// visit renders it, and a lazy chunk would only add a fetch and a skeleton
// flash to the common path.
import LegacyApp from "./legacy/LegacyApp";

// GUNDI-5642: the current portal, in its own chunk so browsers that never take
// the hidden unlock never download it.
const NewApp = React.lazy(() =>
  import("./NewApp").then((module) => {
    // Chunk loaded: a past reload-recovery latch is spent, arm the next one.
    clearLazyTreeReloadLatch();
    return module;
  })
);

// Skeleton while the new-UI chunk downloads: no copy, so it needs no keys.
function NewTreeLoading() {
  return (
    <div className="px-8 desktop:px-[60px] py-6 pt-[115px]" data-testid="NewTreeLoading-div">
      <div className="h-8 w-64 mb-6 bg-neutral-gray-50 rounded animate-pulse" />
      {[...Array(8)].map((_, i) => (
        <div key={i} className="h-4 w-full max-w-3xl mb-3 bg-neutral-gray-50 rounded animate-pulse" />
      ))}
    </div>
  );
}

function NewTreeLoadFailed() {
  const { t } = useTranslation();
  return (
    <div className="px-8 desktop:px-[60px] py-6 pt-[115px] text-base text-off-black" data-testid="NewTreeLoadFailed-div">
      {t("Something went wrong. Please try again or contact support.")}
    </div>
  );
}

// The version flag alone decides which tree owns the paths — both trees keep
// their own full route table, their own Nav, help panel, toasts and 404, and
// only one of them ever mounts. Mounting one tree inside the other's chrome
// instead would put the wrong navbar over the pages.
function VersionedTree() {
  const version = useUiVersion();
  if (version === "new") {
    return (
      <LazyTreeBoundary fallback={<NewTreeLoadFailed />}>
        <Suspense fallback={<NewTreeLoading />}>
          <NewApp />
        </Suspense>
      </LazyTreeBoundary>
    );
  }
  return <LegacyApp />;
}

function App() {
  return (
    <div className="min-h-screen bg-very-light-gray">
      <ReactKeycloakProvider authClient={keycloak} initOptions={keycloakInitOptions}>
        <TimezoneProvider>
          <BrowserRouter>
            <VersionedTree />
          </BrowserRouter>
        </TimezoneProvider>
      </ReactKeycloakProvider>
    </div>
  );
}

export default App;
